import { Dispatch, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppAction, AppState } from '../Components/App/appReducer';
import { ChatMessage } from '../Components/Shared/API/ApiTypes';
import { EdullaConfig } from '../config/configType';
import { DismissKind, dismissProactive, shouldImplicitlyDismiss } from './dismissProactive';

// Launcher-driven half of the proactive UX (the timer-driven half lives in
// useProactiveTimer). The launcher owns the teaser shown while the widget is
// minimized, but cannot run any proactive flow itself (no sessionid, index,
// API key, or state machine), so it delegates the user's gestures here over
// the postMessage bridge:
//   dismissProactive — the user clicked the teaser ✕ (explicit dismiss)
//   widgetMinimized  — the widget was collapsed (implicit-dismiss heuristic)
//   openProactive    — the user clicked the teaser body (open the chat)
export function useProactiveLauncherBridge(
    appState: AppState,
    config: EdullaConfig,
    dispatch: Dispatch<AppAction>
) {
    const navigate = useNavigate();

    // Keep latest state/config available to the (long-lived) message listener
    // below without re-subscribing on every change.
    const appStateRef = useRef(appState);
    const configRef = useRef(config);
    useEffect(() => { appStateRef.current = appState; });
    useEffect(() => { configRef.current = config; });

    useEffect(() => {
        // Both gestures dismiss the same way; they differ only in kind and in
        // whether the launcher supplied the nudge id. Without one, fall back to
        // the latest on-screen nudge.
        const dismissFromLauncher = (kind: DismissKind, launcherMessageId?: string) => {
            const state = appStateRef.current;
            const lastProactive = [...(state.messages ?? [])]
                .filter((m: ChatMessage) => m.proactive)
                .pop();
            dismissProactive(
                {
                    sessionid: state.sessionid,
                    index: configRef.current.IndexName,
                    kind,
                    proactiveMessageId: launcherMessageId ?? lastProactive?.proactiveMessageId,
                    pageUrl: state.pageUrl,
                },
                dispatch
            );
        };

        const handler = (event: MessageEvent) => {
            const msg = event.data as { isEdulla?: boolean; action?: string; data?: any };
            if (!msg?.isEdulla) return;

            if (msg.action === 'dismissProactive') {
                const launcherMessageId =
                    (typeof msg.data?.proactiveMessageId === 'string' && msg.data.proactiveMessageId)
                        ? msg.data.proactiveMessageId
                        : undefined;
                dismissFromLauncher('explicit', launcherMessageId);
                return;
            }

            if (msg.action === 'widgetMinimized') {
                // Implicit dismiss (ADR #55): count the collapse as a dismissal
                // only when a nudge was *shown* within the window and the user did
                // not reply in that window. Otherwise it is an ordinary close.
                const state = appStateRef.current;
                if (!shouldImplicitlyDismiss(
                    { proactiveShownAt: state.proactiveShownAt, messages: state.messages ?? [] },
                    Date.now()
                )) return;
                dismissFromLauncher('implicit');
                return;
            }

            if (msg.action === 'openProactive') {
                // Reveal the conversation; ScrollToBottom keeps the latest
                // (proactive) message in view.
                navigate('/Chat');
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [dispatch, navigate]);
}
